import { createFileRoute } from "@tanstack/react-router";
import { ArticlePage } from "@/components/ArticlePage";
import { articleSchemas } from "@/lib/seo";

const SITE = "https://shopify-freelancer.net";
const PATH = "/ratgeber/shopify-conversion-optimierung";
const TITLE = "Shopify Conversion Optimierung: Leitfaden 2026";
const DESCRIPTION =
  "Shopify Conversion Optimierung Schritt für Schritt: Kennzahlen und die größten Umsatzhebel auf Produktseite, Warenkorb und Checkout.";

const faqs = [
  {
    q: "Was ist eine gute Conversion Rate bei Shopify?",
    a: "Über alle Shops hinweg liegt die Kaufabschlussrate meist zwischen 1 und 3 Prozent. Entscheidend ist aber nicht der Branchenschnitt, sondern Ihr eigener Verlauf: Wenn Sie von 1,4 auf 1,9 Prozent kommen, ist das ein Umsatzplus von rund 35 Prozent bei identischem Traffic.",
  },
  {
    q: "Wo fängt man mit der Conversion Optimierung an?",
    a: "Immer bei der Messung. Erst wenn Add-to-Cart-Rate, Checkout-Abbrüche und Umsatz pro Session sauber vorliegen, wissen Sie, ob das Problem auf der Produktseite, im Warenkorb oder im Checkout liegt.",
  },
  {
    q: "Wie lange dauert es, bis man Ergebnisse sieht?",
    a: "Technische Hebel wie Pagespeed und Checkout-Flow wirken sofort. Für belastbare Aussagen aus A/B-Tests brauchen Sie je nach Traffic zwei bis vier Wochen.",
  },
  {
    q: "Braucht man dafür Apps?",
    a: "Selten. Die meisten Apps kosten Ladezeit und lösen ein Problem, das man auch im Theme sauber lösen kann. Weniger Apps ist in der Praxis häufig der schnellere Conversion-Gewinn.",
  },
];

export const Route = createFileRoute("/ratgeber/shopify-conversion-optimierung")({
  component: Page,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: `${SITE}${PATH}` },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${SITE}${PATH}` }],
    scripts: articleSchemas({ title: TITLE, description: DESCRIPTION, path: PATH, faqs }),
  }),
});

function Page() {
  return (
    <ArticlePage
      kicker="Ratgeber · Conversion"
      title="Shopify Conversion Optimierung: der praktische Leitfaden"
      intro="Mehr Umsatz entsteht selten durch mehr Traffic, sondern durch weniger Reibung auf dem Weg zum Kauf. Dieser Leitfaden zeigt, in welcher Reihenfolge Sie die Hebel angehen und woran Sie erkennen, dass eine Änderung wirklich gewirkt hat."
      source="ratgeber-conversion"
      sections={[
        {
          h: "1. Zuerst messen, dann anfassen",
          p: [
            "Ohne saubere Zahlen ist jede Optimierung Geschmackssache. Bevor irgendetwas am Theme verändert wird, müssen fünf Werte vorliegen: Kaufabschlussrate gesamt und mobil, Add-to-Cart-Rate, Checkout-Abbruchquote, durchschnittlicher Warenkorb und Umsatz pro Session.",
            "Der wichtigste Wert ist Umsatz pro Session. Er verhindert, dass Sie sich über eine höhere Conversion-Rate freuen, während der Warenkorb gleichzeitig schrumpft.",
          ],
          list: [
            "Kaufabschlussrate gesamt und getrennt nach Mobile/Desktop",
            "Add-to-Cart-Rate je Top-Produktseite",
            "Checkout-Abbrüche pro Schritt",
            "Durchschnittlicher Warenkorbwert",
            "Umsatz pro Session als Leitkennzahl",
          ],
        },
        {
          h: "2. Die Produktseite entscheidet",
          p: [
            "Auf der Produktseite fällt die Kaufentscheidung, nicht im Checkout. Typische Verluste entstehen durch unklare Preis- und Versandkommunikation, zu viele gleichwertige Optionen, fehlende Antworten auf Standardeinwände und Bilder, die das Produkt nicht im Einsatz zeigen.",
            "Ein Test, der fast immer etwas bringt: Alles, was oberhalb des ersten Scrolls steht, muss die drei Fragen beantworten, was ist es, warum lohnt es sich, was kostet es inklusive Versand.",
          ],
        },
        {
          h: "3. Warenkorb und Checkout entlasten",
          p: [
            "Die meisten Abbrüche im Checkout haben zwei Ursachen: unerwartete Kosten und zu viele Eingabefelder. Versandkosten und Lieferzeit gehören deshalb schon in den Warenkorb, nicht erst in den letzten Schritt.",
            "Auf Mobile zählt jede Sekunde und jeder Fingertipp. Express-Zahlarten, Autofill und ein Formular ohne überflüssige Felder heben die Abschlussrate oft stärker als jede Designänderung.",
          ],
        },
        {
          h: "4. Geschwindigkeit ist Conversion",
          p: [
            "Jede zusätzliche Sekunde Ladezeit kostet messbar Umsatz. Die größten Bremsen sind in der Praxis nicht die Bilder, sondern Apps und Tracking-Skripte, die niemand mehr braucht.",
            "Ein Inventar aller installierten Apps, mit der Frage, welche davon in den letzten drei Monaten Umsatz erzeugt hat, ist häufig der schnellste Pagespeed-Gewinn.",
          ],
        },
        {
          h: "5. Testen statt diskutieren",
          p: [
            "A/B-Tests sind kein Selbstzweck: Testen Sie nur Änderungen, die genug Traffic sehen, um überhaupt ein Ergebnis liefern zu können. Bei kleineren Shops ist eine klare Vorher-Nachher-Messung über zwei Wochen oft aussagekräftiger als ein unterbesetzter Split-Test.",
          ],
        },
      ]}
      faqs={faqs}
      related={[
        { to: "/blog/shopify-checkout-optimieren", label: "Checkout optimieren" },
        { to: "/roi-rechner", label: "Potenzial berechnen" },
        { to: "/leistungen", label: "Leistungen ansehen" },
      ]}
    />
  );
}
